'use client';

import React from 'react';
import { Steps, UseStepper } from 'headless-stepper';
import { FaCheck } from 'react-icons/fa';

import { cn } from '@/utils/TailwindHelper';

export interface FormStepperOptions {
	canClickSteps?: boolean;
	canJumpToStepsAhead?: boolean;
	hideStepsNumbers?: boolean;
	hideLabels?: boolean;
	hideDisabledSteps?: boolean;
}

interface FormStepperProps {
	steps: Steps[];
	stepper: UseStepper;
	options?: FormStepperOptions;
	className?: string;
}

export const FormStepper = ({ steps = [], stepper, options, className }: FormStepperProps) => {
	const baseStepClassName =
		'flex flex-row gap-2 cursor-pointer aspect-square border-2 relative size-icon-md items-center justify-center rounded-full';

	// State
	const { canClickSteps, canJumpToStepsAhead, hideStepsNumbers, hideLabels, hideDisabledSteps } =
		options ?? {};

	// Debug steps
	// useEffect(() => {
	// 	console.group('%c Stepper steps', 'color: hotpink');
	// 	for (let i = 0; i < steps.length; i++) {
	// 		console.log(`steps[${i}]`, steps[i]);
	// 	}
	// 	console.groupEnd();
	// }, [steps]);

	// Helpers
	const isStepPending = (stepIndex: number) => {
		return steps[stepIndex].isValid == undefined;
	};

	const isStepCurrent = (stepIndex: number) => {
		return stepper.state?.currentStep === stepIndex;
	};

	const isStepSuccess = (stepIndex: number) => {
		return !isStepPending(stepIndex) && steps[stepIndex].isValid;
	};

	const isStepError = (stepIndex: number) => {
		return !isStepPending(stepIndex) && !steps[stepIndex].isValid;
	};

	const isStepDisabled = (stepIndex: number) => {
		return steps[stepIndex].disabled;
	};

	const relativeStepIndex = (stepIndex: number) => {
		if (!hideDisabledSteps) {
			return stepIndex;
		}
		let relativeStep = stepIndex;
		for (let i = 0; i < stepIndex; i++) {
			if (isStepDisabled(i)) {
				relativeStep--;
			}
		}
		return relativeStep;
	};

	return (
		<nav className={cn('flex flex-col', className)} {...stepper.stepperProps}>
			<div className={'bg-neutral-black'}></div>
			{stepper.stepsProps?.map((step, index) => {
				const key = `step-${index}`;
				const isLastStep = index === steps.length - 1;
				const forceSuccess = isLastStep && isStepSuccess(index);
				const showSuccessAccentLine = isStepSuccess(index) && !isLastStep;

				return (
					<React.Fragment key={key}>
						<div className="grid grid-cols-[auto_1fr] gap-2">
							<div className="flex flex-col">
								<div className="relative">
									{isStepCurrent(index) && !forceSuccess ? (
										<a
											key={key}
											{...(canClickSteps ? step : {})}
											className={cn(
												baseStepClassName,
												'cursor-default',
												'bg-light-action-primary-hover',
												'border-light-action-primary-hover',
											)}
										>
											{!hideStepsNumbers && (
												<span className="text-neutral-white font-bold">
													{relativeStepIndex(index) + 1}
												</span>
											)}
										</a>
									) : isStepSuccess(index) ? (
										<a
											key={key}
											{...(canClickSteps ? step : {})}
											className={cn(
												baseStepClassName,
												'bg-light-design-accent',
												'border-light-design-accent',
												!canClickSteps && 'cursor-default',
											)}
										>
											<FaCheck className={'text-neutral-white size-3'} />
										</a>
									) : isStepError(index) ? (
										<a
											key={key}
											{...(canClickSteps ? step : {})}
											className={cn(
												baseStepClassName,
												'bg-status-error/30',
												'border-status-error/5',
												!canClickSteps && 'cursor-default',
											)}
										>
											{!hideStepsNumbers && (
												<span className="text-light-text-lighter font-bold">
													{relativeStepIndex(index) + 1}
												</span>
											)}
										</a>
									) : isStepDisabled(index) ? (
										!hideDisabledSteps ? (
											<a
												key={key}
												{...(canClickSteps ? step : {})}
												className={cn(
													baseStepClassName,
													'bg-neutral-light',
													'border-neutral-lighter',
													'cursor-not-allowed opacity-95',
												)}
											>
												{!hideStepsNumbers && (
													<span className="text-neutral-white font-bold">
														{relativeStepIndex(index) + 1}
													</span>
												)}
											</a>
										) : null
									) : (
										<a
											key={key}
											{...(canClickSteps && canJumpToStepsAhead ? step : {})}
											className={cn(
												baseStepClassName,
												'bg-neutral-light',
												'border-neutral-lighter',
												!(canClickSteps && canJumpToStepsAhead) && 'cursor-default',
											)}
										>
											{!hideStepsNumbers && (
												<span className="text-light-text-lighter font-bold">
													{relativeStepIndex(index) + 1}
												</span>
											)}
										</a>
									)}
								</div>
								{!isLastStep && (
									<div
										className={cn(
											'ml-[11px] min-h-6 w-[2px] flex-grow',
											showSuccessAccentLine ? 'bg-light-action-secondary' : 'bg-neutral-light',
											isStepDisabled(index) && hideDisabledSteps && 'hidden',
										)}
									/>
								)}
							</div>
							<div
								className={cn(
									'flex items-start',
									isStepDisabled(index) && hideDisabledSteps && 'hidden',
								)}
							>
								{!hideLabels && (
									<span
										className={cn(
											'text-light-text-lighter',
											isStepSuccess(index) && 'text-light-heading',
											isStepCurrent(index) && 'text-light-heading font-bold',
											isStepDisabled(index) && 'opacity-50',
											!isLastStep && 'pb-3',
										)}
									>
										{steps[index].label}
									</span>
								)}
							</div>
						</div>
					</React.Fragment>
				);
			})}
		</nav>
	);
};
